import { useState } from 'react'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  Clock,
  Lock,
  PlayCircle,
  ShieldCheck,
  Star,
  Users,
} from 'lucide-react'

import { ClassSessionList } from '@/components/class-sessions'
import { ShareButton } from '@/components/share'
import { Badge } from '@/components/ui/badge'
import { LessonContent } from '@/components/lesson-content'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Textarea } from '@/components/ui/field'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { formatLabel, levelLabel, lessonTypeLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useCourse, useCourseReviews } from '@/features/catalog/api'
import { useEnrollFree, useMyEnrollment, useSubmitReview } from '@/features/learning/api'
import { useCheckout } from '@/features/payment/api'
import { useSession } from '@/features/auth/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatDuration, formatNumber, formatRupiah } from '@/lib/format'
import type {  } from '@/lib/types'

const route = getRouteApi('/public/kelas/$slug')

export function CourseDetailPage() {
  const { slug } = route.useParams()
  const { data: course, isLoading, error } = useCourse(slug)
  const { data: session } = useSession()
  const { data: enrollment } = useMyEnrollment(slug, Boolean(session))

  if (isLoading) return <CourseDetailSkeleton />

  if (error || !course) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Kelas tidak ditemukan"
          description="Tautan mungkin sudah berubah atau kelas telah diarsipkan pengajarnya."
          action={
            <Link to="/kelas">
              <Button variant="outline">Kembali ke katalog</Button>
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="pb-16">
      <section className="border-b border-line bg-sunken">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:py-12">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="brand">{course.category}</Badge>
              <Badge>{levelLabel[course.level] ?? course.level}</Badge>
              <Badge tone="info">{formatLabel[course.format] ?? course.format}</Badge>
            </div>

            <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
              {course.title}
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-muted">{course.subtitle}</p>

            <div className="mt-4">
              <ShareButton title={course.title} text={`Kelas di skava.id: ${course.title}`} />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted">
              <span className="inline-flex items-center gap-1.5 font-semibold text-ink">
                <BadgeCheck className="size-4 text-brand" />
                {course.expert.full_name}
              </span>
              {course.rating_count > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="size-4 fill-amber-400 text-amber-400" />
                  {course.rating.toFixed(1)} dari {formatNumber(course.rating_count)} ulasan
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1.5">
                <Users className="size-4" />
                {formatNumber(course.enrollment_count)} peserta
              </span>
              {course.duration_minutes > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="size-4" />
                  {formatDuration(course.duration_minutes)}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* min-w-0 pada kedua kolom bukan hiasan. Petak grid berukuran otomatis
          tidak pernah menyusut di bawah lebar min-konten isinya, dan judul
          pelajaran di daftar kurikulum memakai truncate — yang berarti
          white-space: nowrap, yang berarti lebar min-kontennya adalah lebar
          seluruh judul. Tanpa ini kolom kiri terkunci di 553px dan seluruh
          halaman tergulir menyamping di setiap lebar ponsel. */}
      <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="min-w-0 space-y-8">
          {enrollment ? (
            <Card>
              <CardBody className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-ink">Anda sudah terdaftar di kelas ini</p>
                  <p className="mt-0.5 text-sm text-muted">
                    Kemajuan {enrollment.progress_percent}% · bergabung {formatDate(enrollment.enrolled_at)}
                  </p>
                </div>
                <Link to="/app/belajar/$enrollmentId" params={{ enrollmentId: enrollment.id }}>
                  <Button>
                    Lanjut belajar
                    <ArrowRight className="size-4" />
                  </Button>
                </Link>
              </CardBody>
            </Card>
          ) : null}

          <section>
            <h2 className="text-xl font-extrabold tracking-tight text-ink">Tentang kelas ini</h2>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-muted">{course.description}</p>
          </section>

          <Curriculum course={course} />

          {/* Jadwal sesi hanya ditampilkan bila kelasnya memang berformat
              langsung; menampilkan bagian kosong pada kelas mandiri hanya
              menimbulkan pertanyaan yang tidak perlu. */}
          {course.format === 'live' || course.format === 'hybrid' ? (
            <section>
              <h2 className="text-xl font-extrabold tracking-tight text-ink">Jadwal sesi langsung</h2>
              <ClassSessionList slug={course.slug} className="mt-4" />
            </section>
          ) : null}
          <Reviews slug={slug} canReview={Boolean(enrollment)} />
        </div>

        {/* Panel pembelian berada pada kolom samping dan menempel saat
            halaman digulir, sehingga tombol beli selalu terjangkau tanpa
            menyisakan ruang kosong di bagian atas. */}
        <aside className="min-w-0 space-y-6 lg:-mt-40">
          <PurchasePanel slug={slug} />

          <Card>
            <CardHeader title="Pengajar" />
            <CardBody>
              <p className="text-base font-bold text-ink">{course.expert.full_name}</p>
              <Link
                to="/talenta/$expertId"
                params={{ expertId: course.expert.id }}
                className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
              >
                Lihat profil lengkap
                <ArrowRight className="size-3.5" />
              </Link>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Ringkasan" />
            <CardBody className="space-y-3 text-sm">
              <SummaryRow label="Materi" value={`${course.lesson_count} pelajaran`} />
              <SummaryRow label="Durasi" value={formatDuration(course.duration_minutes)} />
              <SummaryRow label="Tingkat" value={levelLabel[course.level] ?? course.level} />
              <SummaryRow label="Format" value={formatLabel[course.format] ?? course.format} />
              {course.published_at ? <SummaryRow label="Terbit" value={formatDate(course.published_at)} /> : null}
            </CardBody>
          </Card>
        </aside>
      </div>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted">{label}</span>
      <span className="font-semibold text-ink">{value}</span>
    </div>
  )
}

/** Panel harga dan tombol beli, menempel saat halaman digulir di desktop. */
function PurchasePanel({ slug }: { slug: string }) {
  const { data: course } = useCourse(slug)
  const { data: session } = useSession()
  const { data: enrollment } = useMyEnrollment(slug, Boolean(session))
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const enrollFree = useEnrollFree()
  const navigate = useNavigate()
  const toast = useToast()

  if (!course) return null

  const discounted = course.discount_price !== undefined && course.discount_price < course.price
  const saving = discounted ? course.price - (course.discount_price ?? 0) : 0

  const handleFree = () => {
    if (!session) {
      navigate({ to: '/masuk', search: { redirect: `/kelas/${slug}` } })
      return
    }

    enrollFree.mutate(course.id, {
      onSuccess: (data) => {
        toast.success('Kelas terbuka', 'Selamat belajar!')
        navigate({ to: '/app/belajar/$enrollmentId', params: { enrollmentId: data.enrollment.id } })
      },
      onError: (error) => toast.error('Gagal mendaftar', error instanceof ApiError ? error.message : undefined),
    })
  }

  return (
    <div className="lg:sticky lg:top-24">
      <Card className="overflow-hidden shadow-[var(--shadow-lifted)]">
        {course.thumbnail_url ? (
          <img src={course.thumbnail_url} alt="" className="aspect-[16/9] w-full object-cover" />
        ) : null}

        <CardBody>
          {discounted ? (
            <div className="mb-2 flex items-center gap-2">
              <span className="text-sm text-subtle line-through">{formatRupiah(course.price)}</span>
              <Badge tone="success">Hemat {formatRupiah(saving)}</Badge>
            </div>
          ) : null}

          <p className="text-3xl font-extrabold tracking-tight text-ink">
            {course.effective_price === 0 ? 'Gratis' : formatRupiah(course.effective_price)}
          </p>

          <div className="mt-5 space-y-2.5">
            {enrollment ? (
              <Link to="/app/belajar/$enrollmentId" params={{ enrollmentId: enrollment.id }} className="block">
                <Button fullWidth size="lg">
                  Lanjut belajar
                </Button>
              </Link>
            ) : course.effective_price === 0 ? (
              <Button fullWidth size="lg" loading={enrollFree.isPending} onClick={handleFree}>
                Ikuti kelas gratis
              </Button>
            ) : (
              <Button
                fullWidth
                size="lg"
                onClick={() => {
                  if (!session) {
                    navigate({ to: '/masuk', search: { redirect: `/kelas/${slug}` } })
                    return
                  }
                  setCheckoutOpen(true)
                }}
              >
                Beli kelas ini
              </Button>
            )}

            <p className="text-center text-xs text-subtle">
              Akses terbuka otomatis setelah pembayaran terkonfirmasi.
            </p>
          </div>

          <ul className="mt-5 space-y-2 border-t border-line pt-4 text-sm text-muted">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
              Akses seumur waktu kelas berlangsung
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
              Materi terbuka bertahap sesuai jadwal kelas
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
              Sertifikat setelah memenuhi kriteria kelulusan
            </li>
          </ul>
        </CardBody>
      </Card>

      <CheckoutDialog
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        courseId={course.id}
        courseTitle={course.title}
        amount={course.effective_price}
      />
    </div>
  )
}



function CheckoutDialog({
  open,
  onClose,
  courseId,
  courseTitle,
  amount,
}: {
  open: boolean
  onClose: () => void
  courseId: string
  courseTitle: string
  amount: number
}) {
  const checkout = useCheckout()
  const navigate = useNavigate()
  const toast = useToast()

  const submit = () => {
    // Metode tidak dikirim sama sekali: pembeli memilihnya di halaman
    // Midtrans, yang selalu menampilkan seluruh saluran aktif pada akun
    // merchant. Menyalin daftar itu ke sini berarti daftarnya harus ikut
    // diperbarui setiap kali saluran baru diaktifkan — dan yang terlupa
    // memperbarui akan menyembunyikan saluran yang sebenarnya tersedia.
    checkout.mutate(
      { course_id: courseId },
      {
        onSuccess: (data) => {
          onClose()
          navigate({ to: '/checkout/$orderId', params: { orderId: data.order.id } })
        },
        onError: (error) => {
          toast.error(
            'Checkout gagal',
            error instanceof ApiError ? error.message : 'Coba beberapa saat lagi.',
          )
        },
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Konfirmasi pembelian"
      description={courseTitle}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button loading={checkout.isPending} onClick={submit}>
            Lanjut ke pembayaran
          </Button>
        </>
      }
    >
      <div className="rounded-2xl border border-line p-5">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-muted">Total tagihan</span>
          <span className="text-2xl font-bold tracking-[-0.03em] text-ink">{formatRupiah(amount)}</span>
        </div>

        <div className="mt-5 flex gap-3 border-t border-line pt-5">
          <ShieldCheck className="mt-0.5 size-4.5 shrink-0 text-brand" />
          <p className="text-sm leading-relaxed text-muted">
            Anda akan diarahkan ke halaman pembayaran Midtrans. Pilih sendiri caranya di sana — transfer
            bank, QRIS, e-wallet, atau kartu. Akses kelas terbuka otomatis begitu pembayaran diterima.
          </p>
        </div>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-subtle">
        Tagihan berlaku 24 jam. Riwayat pembayaran Anda tersimpan di halaman Pesanan.
      </p>
    </Dialog>
  )
}

function Curriculum({ course }: { course: NonNullable<ReturnType<typeof useCourse>['data']> }) {
  const [preview, setPreview] = useState<{ title: string; content?: string } | null>(null)
  const modules = course.modules ?? []

  if (modules.length === 0) return null

  return (
    <section>
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-xl font-extrabold tracking-tight text-ink">Kurikulum</h2>
        <p className="text-sm text-muted">
          {modules.length} modul · {course.lesson_count} pelajaran
        </p>
      </div>

      <div className="mt-4 space-y-3">
        {modules.map((module, index) => (
          <details key={module.id} className="card group overflow-hidden" open={index === 0}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 marker:hidden">
              <div className="min-w-0">
                <p className="text-sm font-bold text-ink">
                  Modul {module.sequence}: {module.title}
                </p>
                {module.summary ? (
                  <p className="mt-0.5 line-clamp-2 text-sm text-muted">{module.summary}</p>
                ) : null}
              </div>
              <span className="flex shrink-0 items-center gap-3 text-sm text-muted">
                {module.lessons.length} materi
                <ChevronDown className="size-4 transition group-open:rotate-180" />
              </span>
            </summary>

            <ul className="divide-y divide-[var(--border)] border-t border-line">
              {module.lessons.map((lesson) => (
                <li key={lesson.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    {lesson.is_preview ? (
                      <PlayCircle className="size-4 shrink-0 text-brand" />
                    ) : (
                      <Lock className="size-4 shrink-0 text-subtle" />
                    )}
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium text-ink">{lesson.title}</p>
                      <p className="text-xs text-subtle">
                        {lessonTypeLabel[lesson.content_type] ?? lesson.content_type}
                        {lesson.duration_minutes > 0 ? ` · ${formatDuration(lesson.duration_minutes)}` : ''}
                        {lesson.drip_days > 0 ? ` · terbuka H+${lesson.drip_days}` : ''}
                      </p>
                    </div>
                  </div>

                  {lesson.is_preview ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="shrink-0"
                      onClick={() => setPreview({ title: lesson.title, content: lesson.content })}
                    >
                      Pratinjau
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>

      <Dialog
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.title ?? ''}
        description="Pratinjau gratis"
      >
        <div className="aspect-video w-full rounded-xl bg-inverted/90" />
        <p className="mt-4 leading-relaxed text-muted">
          {preview?.content ? (
            <LessonContent content={preview.content} />
          ) : (
            'Materi ini akan tersedia setelah Anda bergabung ke kelas.'
          )}
        </p>
      </Dialog>
    </section>
  )
}

function Reviews({ slug, canReview }: { slug: string; canReview: boolean }) {
  const { data } = useCourseReviews(slug)
  const submitReview = useSubmitReview(slug)
  const toast = useToast()
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState('')

  const reviews = data?.reviews ?? []

  return (
    <section>
      <h2 className="text-xl font-extrabold tracking-tight text-ink">Ulasan peserta</h2>

      {canReview ? (
        <Card className="mt-4">
          <CardBody className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-ink">Bagaimana pengalaman Anda?</p>
              <div className="mt-2 flex gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRating(value)}
                    aria-label={`Beri ${value} bintang`}
                    className="rounded-md p-1 transition hover:scale-110"
                  >
                    <Star
                      className={`size-6 ${value <= rating ? 'fill-amber-400 text-amber-400' : 'text-subtle'}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <Textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Ceritakan bagian yang paling membantu, atau yang masih bisa diperbaiki."
              rows={3}
            />

            <Button
              loading={submitReview.isPending}
              onClick={() =>
                submitReview.mutate(
                  { rating, comment },
                  {
                    onSuccess: () => {
                      toast.success('Ulasan tersimpan', 'Terima kasih atas masukannya.')
                      setComment('')
                    },
                    onError: (error) =>
                      toast.error('Gagal menyimpan ulasan', error instanceof ApiError ? error.message : undefined),
                  },
                )
              }
            >
              Kirim ulasan
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {reviews.length === 0 ? (
        <p className="mt-4 text-sm text-muted">Belum ada ulasan. Jadilah yang pertama setelah mengikuti kelas.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {reviews.map((review) => (
            <li key={review.id} className="card p-5">
              <div className="flex items-center justify-between gap-4">
                <p className="font-semibold text-ink">{review.author_name}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-ink">
                  <Star className="size-4 fill-amber-400 text-amber-400" />
                  {review.rating}
                </span>
              </div>
              {review.comment ? <p className="mt-2 leading-relaxed text-muted">{review.comment}</p> : null}
              <p className="mt-2 text-xs text-subtle">{formatDate(review.created_at)}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function CourseDetailSkeleton() {
  return (
    <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1.6fr_1fr] [&>*]:min-w-0">
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-5 w-2/3" />
        <SkeletonText lines={6} className="pt-6" />
      </div>
      <Skeleton className="h-96 w-full rounded-2xl" />
    </div>
  )
}
